import { useEffect, useMemo } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'

import { GET_CHARTS_BY_LABEL_ID_AND_COUNTRY, GET_CHARTS_BY_SPACE_ID_AND_COUNTRY } from 'modules/Charts/graphql/queries'
import { countryCodeVar, currentUserVar, labelCodeIdVar, spaceIdVar } from 'modulesCommon/store'
import { sortFactSources } from 'shared/utils/factSources'
import { rankCharts } from 'shared/utils/charts'
import { chartsListVar, factSourcesListVar } from '../store'

import { IChart, IChartLabelResult, IChartLookup } from 'shared/interfaces'

const useCharts = () => {
  const countryCode = useReactiveVar(countryCodeVar)
  const currentUser = useReactiveVar(currentUserVar)
  const chartRows = useReactiveVar(chartsListVar)
  const factSources = useReactiveVar(factSourcesListVar)
  const currentLabelId = useReactiveVar(labelCodeIdVar)
  const spaceId = useReactiveVar(spaceIdVar)

  const {
    data: chartsData,
    loading: chartsLoading,
    error: chartsError
  } = useQuery<{ getChartsByLabelIdAndCountry: IChartLabelResult[]; getChartsBySpaceIdAndCountry: IChartLabelResult[] }>(currentLabelId ? GET_CHARTS_BY_LABEL_ID_AND_COUNTRY : GET_CHARTS_BY_SPACE_ID_AND_COUNTRY, {
    variables: { countryCode, ...(spaceId && { space_id: spaceId }), ...(currentLabelId && { label_id: currentLabelId }) },
    skip: (!countryCode) || (!spaceId && !currentLabelId),
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    const source = chartsData?.getChartsByLabelIdAndCountry || chartsData?.getChartsBySpaceIdAndCountry
    if (source) {
      const { charts, factSources } = (source ?? []).reduce(
        (res: { charts: IChart[]; factSources: IChartLookup[] }, { data, chart }) => ({
          charts: [...res.charts, ...data],
          factSources: [
            ...res.factSources,
            {
              ...chart,
              configuration: chart.configuration.toLowerCase(),
              country_code: countryCode
            }
          ]
        }),
        {
          charts: [],
          factSources: []
        }
      )
      const chartsDataList = rankCharts(charts, currentLabelId)
      const factSourcesList = factSources.sort(sortFactSources)
      chartsListVar(chartsDataList)
      factSourcesListVar(factSourcesList)
    }
  }, [chartsData, currentLabelId, countryCode])

  const assignedСampaigns = useMemo(
    (): null | string[] => currentUser?.assigned_campaigns?.map((item) => item.id) || null,
    [currentUser]
  )

  useLoading({ isLoading: chartsLoading || !chartRows.length || !factSources.length })
  useServerError([chartsError])

  return { chartRows, assignedСampaigns, factSources, chartsLoading }
}

export default useCharts
